import React, { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMediaUrl } from "../../../../../utils/Media";
import { ConversationMessage } from "../../../../../model/integration/dto/meta/MetaConversationMessage";
import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";
import { Spinner } from "../../../../../components/Spinner";
import { WAMediaRetentionInfo } from "./WAMediaRetentionInfo";
import { WAMediaUnavailable } from "./WAMediaUnavailable";
import { formatMediaDuration } from "./media-duration";
import { isMediaPending } from "./media-state";
import { faPause } from "@fortawesome/free-solid-svg-icons/faPause";
import { faPlay } from "@fortawesome/free-solid-svg-icons/faPlay";
import styled from "styled-components";

import { Slider } from "../../../../../utils/local-antd";
import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { Clickable } from "../../../../../components/Clickable";
import { Rounded } from "../../../../../theme/Rounded";

/** Sits inside the message bubble, which already provides the background - it
 * used to paint its own dark box on top, at a fixed 260px.
 */
const Player = styled(Flex)`
  width: 13rem;
  max-width: 100%;
  height: 1.75rem;
`;

const PlayButton = styled(Clickable)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  border-radius: ${Rounded.circle};
  background-color: rgba(128, 128, 128, 0.25);
  color: inherit;

  &:disabled {
    opacity: 0.45;
  }
`;

const TrackSlider = styled(Slider)`
  flex: 1;
  margin: 0;
`;

type Props = {
  message: ConversationMessage;
};

export const WAAudioMessage: React.FC<Props> = ({ message }) => {
  const { mediaId } = message;

  const { t } = useTranslation();
  const [src, setSrc] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const audioRef = useRef<HTMLAudioElement>(null);
  // Only a play the person actually asked for should start on its own. src also
  // changes on its own - a voice note we just sent swaps its local recording
  // for our stored copy - and that used to play itself back at the sender.
  const playWhenReadyRef = useRef(false);

  const ensureDownloaded = async () => {
    if (src || isDownloading || !mediaId) {
      return;
    }

    setIsDownloading(true);

    try {
      playWhenReadyRef.current = true;
      setSrc(getMediaUrl(mediaId!));
    } finally {
      setIsDownloading(false);
    }
  };

  const togglePlayback = async () => {
    if (!src) {
      await ensureDownloaded();
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      await audio.play();
      setIsPlaying(true);
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    if (!src || !playWhenReadyRef.current) {
      return;
    }

    playWhenReadyRef.current = false;

    audioRef.current?.play();
    setIsPlaying(true);
  }, [src]);

  if (!mediaId) {
    return (
      <WAMediaUnavailable
        label={t("common.whatsapp.audio")}
        expired={Boolean(message.mediaExpiredAt)}
        pending={isMediaPending(message)}
      />
    );
  }

  // Nothing is loaded before the first play, so the length the server stored
  // stands in until the file can say for itself
  const length = duration || message.mediaDuration || 0;

  return (
    <Fragment>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        style={{ display: "none" }}
        onLoadedMetadata={(e) => {
          setDuration(e.currentTarget.duration);
        }}
        onTimeUpdate={(e) => {
          setCurrentTime(e.currentTarget.currentTime);
        }}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onEnded={() => {
          setCurrentTime(0);
          setIsPlaying(false);
        }}
      />

      <Player alignItems="center" gap="s">
        {isDownloading ? (
          <Spinner color="main" />
        ) : (
          <PlayButton onClick={togglePlayback} disabled={!mediaId}>
            <AtiraIcon icon={isPlaying ? faPause : faPlay} size="sm" />
          </PlayButton>
        )}

        <TrackSlider
          value={currentTime}
          max={length || 1}
          tooltip={{ open: false }}
          onChange={(value) => {
            if (!audioRef.current) return;

            audioRef.current.currentTime = value;
            setCurrentTime(value);
          }}
        />

        {/* Idle shows how long it is, rounded up as WhatsApp does; playing
            shows where it has got to */}
        <Text fontSize="xs" style={{ whiteSpace: "nowrap", opacity: 0.75 }}>
          {formatMediaDuration(
            isPlaying || currentTime ? currentTime : Math.ceil(length),
          )}
        </Text>

        <WAMediaRetentionInfo createdAt={message.createdAt} variant="inline" />
      </Player>
    </Fragment>
  );
};
